/* V158 — Workspace Agents Stats Widget.
   User-facing card embedded in AgentOsWorkspaceDashboard. Shows:
   - Active agents (primary + additional) with personal runs / saved €
   - Available agents marketplace teaser (top 6)
   - Trial badge if trial still active

   Powered by GET /api/agents/my-stats?workspace_id=XXX&days=30
*/
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MyAgentsStatsWidget({ workspaceId, days = 30 }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    setLoading(true);
    setErr("");
    axios
      .get(`${API}/agents/my-stats?workspace_id=${encodeURIComponent(workspaceId)}&days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Štatistiky agentov nedostupné."))
      .finally(() => setLoading(false));
  }, [workspaceId, days]);

  if (!workspaceId) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm" data-testid="my-agents-stats">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-black text-gray-900">🤖 Tvoji AI Agenti</h3>
        <span className="text-[10px] tracking-widest uppercase font-bold text-gray-500">
          Posledných {days} dní
        </span>
      </div>

      {loading && <p className="text-xs text-gray-500">Načítavam…</p>}
      {err && <p className="text-xs text-red-500">{err}</p>}

      {data && (
        <>
          {/* Top totals */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <MiniTile label="Aktívnych" value={data.totals?.agent_count || 0} accent="text-gray-900" />
            <MiniTile label="Behov" value={data.totals?.runs_30d || 0} accent="text-blue-600" />
            <MiniTile
              label="Ušetrené"
              value={`€${(data.totals?.est_saved_eur_30d || 0).toLocaleString("sk-SK")}`}
              accent="text-emerald-600"
            />
          </div>

          {/* My agents list */}
          {data.my_agents?.length === 0 ? (
            <p className="text-xs text-gray-500 mb-3">
              Zatiaľ nemáš aktivovaného agenta. Pozri si nižšie ponuku.
            </p>
          ) : (
            <div className="space-y-1.5 mb-4">
              {data.my_agents.map((a) => (
                <div
                  key={a.slug}
                  className="flex items-center gap-3 bg-gray-50 border border-gray-100 p-2.5 rounded-lg"
                  data-testid={`my-agent-${a.slug}`}
                >
                  <span className="text-xl">{a.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900 truncate">{a.name}</div>
                    <div className="text-[10px] text-gray-500">€{a.price_eur_month}/mes</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-black text-blue-600">{a.runs_30d}</div>
                    <div className="text-[9px] uppercase tracking-widest text-gray-400">behov</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-black text-emerald-600">
                      €{a.est_saved_eur_30d.toLocaleString("sk-SK")}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-gray-400">ušetrené</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Available marketplace teaser */}
          {data.available_agents?.length > 0 && (
            <div className="border-t border-gray-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] tracking-widest uppercase font-bold text-gray-500">
                  Voľní agenti
                </span>
                <a
                  href="/agents"
                  className="text-[11px] font-bold text-amber-600 hover:text-amber-700"
                  data-testid="agents-marketplace-link"
                >
                  Marketplace →
                </a>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {data.available_agents.map((a) => (
                  <a
                    key={a.slug}
                    href={`/agents/${a.slug}`}
                    className="bg-white border border-gray-200 hover:border-amber-500 hover:bg-amber-50 p-2 text-center transition-colors rounded-lg"
                    data-testid={`available-agent-${a.slug}`}
                    title={a.name}
                  >
                    <div className="text-lg">{a.emoji}</div>
                    <div className="text-[10px] text-gray-700 mt-0.5 truncate">€{a.price_eur_month}</div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const MiniTile = ({ label, value, accent }) => (
  <div className="bg-gray-50 border border-gray-100 p-2 rounded-lg text-center">
    <div className={`text-lg font-black ${accent}`}>{value}</div>
    <div className="text-[9px] tracking-widest uppercase font-bold text-gray-500 mt-0.5">{label}</div>
  </div>
);
